/**
 * Access Requests — admin review queue for brands asking for the Shopify app.
 *
 * Two things this screen has to do that it previously did not:
 *
 *   1. Show the admin what the decision rests on. The applicant fills in a brand
 *      description, a website and an expected monthly order volume specifically to
 *      justify the request; all three reached the browser and were rendered nowhere, so
 *      approve/reject was being clicked on a name and an email address. The same applied
 *      to reviewNotes — the admin could write a rejection reason but could never read one
 *      back, including its own.
 *
 *   2. Reach past the first page. The list endpoint returns {requests, total, page,
 *      limit} and the server defaults limit to 50; this page read only `.requests` and
 *      sent no page, so request 51 onwards simply did not exist as far as the console was
 *      concerned. `total` is the count field the API actually sends — there is no
 *      `totalPages` and no `currentPage` on this endpoint, so both are derived here from
 *      `total` and the limit we asked for.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Page, Card, IndexTable, Text, Badge, Button, InlineStack, BlockStack,
  Modal, TextField, Banner, Tabs, Box, Spinner, Divider, Link,
} from '@shopify/polaris';
import { useQueryClient } from '@tanstack/react-query';
import apiClient from '../utils/api';
import { headingsFor, emptyRow, PageSizeSelect } from '../utils/table';
import { QueryKeys } from '../utils/queries';

/**
 * What the table shows before anyone chooses — the size this screen has always requested, and
 * the one Invoices opens on. Deliberately NOT DEFAULT_PAGE_SIZE: seeding the shared 10 would
 * halve everybody's window on a screen where nobody asked for a change.
 *
 * KNOWN WRINKLE: 20 is not one of the shared control's options (5/10/25/50, in
 * utils/table.jsx, which is not ours to change). The REQUEST is correct — 20 rows arrive and
 * 20 rows are shown — but until someone picks a size the Select has no option to select and
 * displays its first one. Choosing any value makes the two agree for good. Adding 20 to
 * PAGE_SIZE_OPTIONS would settle it in one line, in that shared file.
 */
const PAGE_SIZE = 20;

const TABS = [
  { id: 'pending', content: 'Pending' },
  { id: 'approved', content: 'Approved' },
  { id: 'rejected', content: 'Rejected' },
  { id: 'all', content: 'All' },
];

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString() : '—');
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : '—');

/** A labelled value, laid out as in the Connector order-detail modal. */
function Field({ label, children, span }) {
  return (
    <BlockStack gap="100">
      <Text variant="bodySm" tone="subdued">{label}</Text>
      {typeof children === 'string' || typeof children === 'number' ? (
        <Text variant="bodyMd" breakWord={span}>{children}</Text>
      ) : (
        children
      )}
    </BlockStack>
  );
}

/**
 * Column definitions, so the table keeps its headings with no requests pending.
 * See utils/table.jsx.
 */
const REQUEST_COLUMNS = [
  { key: 'brand', title: 'Brand', tip: 'The store that asked for access, as it named itself on the request.' },
  { key: 'contact', title: 'Contact', tip: 'Who submitted the request.' },
  { key: 'email', title: 'Email', tip: 'Where a decision on this request will be sent.' },
  { key: 'retailers', title: 'Retailers', tip: 'Which retailers the brand said it wants to sell on. This is what they asked for, not what has been configured for them.' },
  { key: 'monthly', title: 'Monthly orders', tip: 'The order volume the brand reported. Self-declared on the request form, not measured.' },
  { key: 'status', title: 'Status', tip: 'Pending, approved or rejected. A rejected brand can submit again.' },
  { key: 'date', title: 'Date', tip: 'When the request was submitted.' },
  { key: 'actions', title: 'Actions', tip: 'Approve or reject. Approving lets the brand install and start syncing.' },
];

export default function AccessRequests() {
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  /**
   * Rows per page — and the API's `limit`.
   *
   * This list is paged SERVER-SIDE: GET /access-requests takes `page` and `limit` and answers
   * with `total`, so the chosen size goes into the request and the fetch re-fires (it is a
   * dependency of `fetchRequests`). Slicing the returned array instead would show 5 of the 20
   * rows the server sent while the pager counted pages of 20.
   */
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [processing, setProcessing] = useState(null);
  const [error, setError] = useState(null);

  const statusFilter = TABS[selectedTab].id === 'all' ? undefined : TABS[selectedTab].id.toUpperCase();

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize };
      if (statusFilter) params.status = statusFilter;
      const result = await apiClient.getAccessRequests(params);
      setRequests(Array.isArray(result?.requests) ? result.requests : []);
      // `total` is the row count for the current filter, not a page count.
      setTotal(Number.isFinite(result?.total) ? result.total : 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, pageSize]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));

  // A filter change resets to page 1: staying on page 3 while switching from All to
  // Pending is how you land on an empty table that looks like an error.
  const handleTabChange = useCallback((index) => {
    setSelectedTab(index);
    setPage(1);
  }, []);

  // A new row count resets it for the same reason, and one of its own: page 3 of a 20-row
  // window is inside page 1 of a 50-row one, so keeping the number would skip 100 requests —
  // or ask the server for a page past the end, which comes back with no rows.
  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setPage(1);
  }, []);

  // If the last row on the last page is acted on, the page can fall past the end.
  useEffect(() => {
    if (!loading && page > totalPages) setPage(totalPages);
  }, [loading, page, totalPages]);

  /**
   * What a decision on a request changes elsewhere in the console.
   *
   * This page fetches its own rows and does not go through React Query, so nothing it does
   * reaches the two caches that a decision here invalidates:
   *
   *   the pending count  - the sidebar badge, which would keep counting a request that has
   *                        just been dealt with until its own refetch came round;
   *   the brand list     - the sidebar lists APPROVED brands only, so approving one is
   *                        precisely when it should appear. useBrands holds its data for five
   *                        minutes and does not refetch on focus, so without this the brand an
   *                        admin has just let in stays invisible until a page reload.
   *
   * Invalidated on a rejection too: the count changes either way, and a rejected request is
   * no more pending than an approved one.
   */
  const queryClient = useQueryClient();
  const refreshSidebar = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: QueryKeys.pendingAccessRequestCount });
    queryClient.invalidateQueries({ queryKey: QueryKeys.brands });
  }, [queryClient]);

  const handleApprove = useCallback(async (id) => {
    setProcessing(id);
    setError(null);
    try {
      await apiClient.approveAccessRequest(id);
      setSelectedRequest(null);
      refreshSidebar();
      await fetchRequests();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(null);
    }
  }, [fetchRequests, refreshSidebar]);

  const handleReject = useCallback(async () => {
    if (!rejectingId) return;
    setProcessing(rejectingId);
    setError(null);
    try {
      await apiClient.rejectAccessRequest(rejectingId, rejectNotes);
      setRejectModalOpen(false);
      setRejectingId(null);
      setRejectNotes('');
      setSelectedRequest(null);
      refreshSidebar();
      await fetchRequests();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(null);
    }
  }, [rejectingId, rejectNotes, fetchRequests, refreshSidebar]);

  const openRejectModal = useCallback((id) => {
    setRejectingId(id);
    setRejectNotes('');
    // Close the detail view first — stacked Polaris modals trap focus in the wrong one.
    setSelectedRequest(null);
    setRejectModalOpen(true);
  }, []);

  const statusBadge = (status) => {
    const tones = { PENDING: 'attention', APPROVED: 'success', REJECTED: 'critical' };
    return <Badge tone={tones[status] || 'info'}>{status}</Badge>;
  };

  const rowMarkup = requests.map((req, index) => (
    <IndexTable.Row
      id={req.id}
      key={req.id}
      position={index}
      onClick={() => setSelectedRequest(req)}
    >
      <IndexTable.Cell>
        <BlockStack gap="050">
          <Text variant="bodyMd" fontWeight="bold">{req.brand?.name || '—'}</Text>
          {req.brand?.shopDomain && (
            <Text variant="bodySm" tone="subdued">{req.brand.shopDomain}</Text>
          )}
        </BlockStack>
      </IndexTable.Cell>
      <IndexTable.Cell>{req.contactName}</IndexTable.Cell>
      <IndexTable.Cell>{req.contactEmail}</IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodySm" tone="subdued">
          {req.marketplaces?.join(', ') || '—'}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodySm">{req.monthlyOrders || '—'}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{statusBadge(req.status)}</IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodySm" tone="subdued">
          {fmtDate(req.createdAt)}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {/* The row itself opens the detail view, so the buttons have to stop the click
            from reaching it — otherwise Approve also pops a modal over the result. */}
        <div onClick={(e) => e.stopPropagation()}>
          <InlineStack gap="200">
            <Button size="slim" onClick={() => setSelectedRequest(req)}>
              Details
            </Button>
            {req.status === 'PENDING' && (
              <>
                <Button
                  size="slim"
                  variant="primary"
                  onClick={() => handleApprove(req.id)}
                  loading={processing === req.id}
                >
                  Approve
                </Button>
                <Button
                  size="slim"
                  tone="critical"
                  onClick={() => openRejectModal(req.id)}
                  disabled={processing === req.id}
                >
                  Reject
                </Button>
              </>
            )}
          </InlineStack>
        </div>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  const detail = selectedRequest;
  const detailActions = useMemo(() => {
    if (!detail || detail.status !== 'PENDING') return {};
    return {
      primaryAction: {
        content: 'Approve',
        onAction: () => handleApprove(detail.id),
        loading: processing === detail.id,
      },
      secondaryActions: [
        {
          content: 'Reject',
          destructive: true,
          onAction: () => openRejectModal(detail.id),
          disabled: processing === detail.id,
        },
      ],
    };
  }, [detail, processing, handleApprove, openRejectModal]);

  return (
    <Page
      fullWidth
      title="Access Requests"
      subtitle={
        total
          ? `Manage brand access to 2W — ${total} ${TABS[selectedTab].id === 'all' ? 'total' : TABS[selectedTab].id}`
          : 'Manage brand access to 2W'
      }
    >
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}

        <Tabs tabs={TABS} selected={selectedTab} onSelect={handleTabChange}>
          <Box paddingBlockStart="400">
            <Card>
              {loading ? (
                <Box padding="600">
                  <InlineStack align="center"><Spinner size="large" /></InlineStack>
                </Box>
              ) : (
                <IndexTable
                  resourceName={{ singular: 'request', plural: 'requests' }}
                  itemCount={requests.length || 1}
                  headings={headingsFor(REQUEST_COLUMNS)}
                  selectable={false}
                  pagination={
                    totalPages > 1
                      ? {
                          hasPrevious: page > 1,
                          hasNext: page < totalPages,
                          onPrevious: () => setPage((p) => Math.max(1, p - 1)),
                          onNext: () => setPage((p) => Math.min(totalPages, p + 1)),
                          label: `Page ${page} of ${totalPages}`,
                        }
                      : undefined
                  }
                  // No emptyState prop: it replaced the whole table, headings included,
                  // which is the defect being fixed. The empty case is a row below.
                >
                  {requests.length === 0 ? emptyRow(
                    REQUEST_COLUMNS, 'requests',
                    'No access request matches. Each row will be one brand asking for access, with what it said it wants to sell.',
                  ) : rowMarkup}
                </IndexTable>
              )}
              {/* Rows per page under the table. The pager stays where it is — IndexTable's own
                  footer — and this sits below it; only once there are rows, because a row
                  count over an empty table is a control with nothing to control. It is kept
                  at size 50, where the pager may vanish, since it is the way back to a
                  shorter window. */}
              {!loading && requests.length > 0 && (
                <Box padding="300">
                  <InlineStack align="start" blockAlign="center">
                    <PageSizeSelect value={pageSize} onChange={handlePageSizeChange} />
                  </InlineStack>
                </Box>
              )}
            </Card>
          </Box>
        </Tabs>

        {/* ---------------- Detail view ---------------- */}
        {detail && (
          <Modal
            open={!!detail}
            onClose={() => setSelectedRequest(null)}
            title={detail.brand?.name || 'Access request'}
            large
            {...detailActions}
          >
            <Modal.Section>
              <BlockStack gap="400">
                <InlineStack gap="400" blockAlign="center" wrap>
                  {statusBadge(detail.status)}
                  <Text variant="bodySm" tone="subdued">
                    Submitted {fmtDateTime(detail.createdAt)}
                  </Text>
                </InlineStack>
                <Divider />

                {/* The shop this request is for */}
                <Text variant="headingSm" as="h4">Shop</Text>
                <InlineStack gap="600" wrap>
                  <Field label="Shop domain">{detail.brand?.shopDomain || '—'}</Field>
                  <Field label="Shop email">{detail.brand?.shopEmail || '—'}</Field>
                </InlineStack>
                <Divider />

                {/* Who asked */}
                <Text variant="headingSm" as="h4">Contact</Text>
                <InlineStack gap="600" wrap>
                  <Field label="Name">{detail.contactName || '—'}</Field>
                  <Field label="Email">
                    {detail.contactEmail ? (
                      <Link url={`mailto:${detail.contactEmail}`}>{detail.contactEmail}</Link>
                    ) : (
                      <Text variant="bodyMd">—</Text>
                    )}
                  </Field>
                </InlineStack>
                <Divider />

                {/* The three fields the applicant filled in to justify the request —
                    the whole point of the review, and previously invisible. */}
                <Text variant="headingSm" as="h4">Application</Text>
                <InlineStack gap="600" wrap>
                  <Field label="Website">
                    {detail.websiteUrl ? (
                      <Link url={detail.websiteUrl} target="_blank">{detail.websiteUrl}</Link>
                    ) : (
                      <Text variant="bodyMd">—</Text>
                    )}
                  </Field>
                  <Field label="Expected monthly orders">{detail.monthlyOrders || '—'}</Field>
                  <Field label="Retailers requested">
                    <Text variant="bodyMd">{detail.marketplaces?.join(', ') || '—'}</Text>
                  </Field>
                </InlineStack>
                <Field label="Brand description" span>
                  {detail.brandDescription || 'The applicant left this blank.'}
                </Field>

                {/* Read-back of the decision, including this console's own past notes */}
                {(detail.reviewedBy || detail.reviewedAt || detail.reviewNotes) && (
                  <>
                    <Divider />
                    <Text variant="headingSm" as="h4">Review</Text>
                    <InlineStack gap="600" wrap>
                      <Field label="Reviewed by">{detail.reviewedBy || '—'}</Field>
                      <Field label="Reviewed at">{fmtDateTime(detail.reviewedAt)}</Field>
                    </InlineStack>
                    <Field label="Review notes" span>
                      {detail.reviewNotes || 'No notes were recorded.'}
                    </Field>
                  </>
                )}
              </BlockStack>
            </Modal.Section>
          </Modal>
        )}

        {/* ---------------- Reject ---------------- */}
        <Modal
          open={rejectModalOpen}
          onClose={() => setRejectModalOpen(false)}
          title="Reject Access Request"
          primaryAction={{
            content: 'Reject',
            destructive: true,
            onAction: handleReject,
            loading: !!processing,
          }}
          secondaryActions={[{ content: 'Cancel', onAction: () => setRejectModalOpen(false) }]}
        >
          <Modal.Section>
            <TextField
              label="Rejection Notes (optional)"
              value={rejectNotes}
              onChange={setRejectNotes}
              multiline={3}
              helpText="These notes will be visible to the brand in the app, and on this request's detail view."
            />
          </Modal.Section>
        </Modal>
      </BlockStack>
    </Page>
  );
}
